import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney } from '@lombok/shared';
import type { Quote, QuoteStatus } from '@lombok/shared';
import { Button, Card } from '@lombok/ui';
import { useCanCreateOrders } from '@/auth/useCanCreateOrders';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill, statusLabel } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { useAccountNames, useAllStatusList, useQuotes } from '@/data/hooks';
import { api } from '@/lib/api';
import { quoteLabel } from '@/lib/names';
import { QUOTE_STATUS_TABS } from './labels';
import { SortSelect } from '@/components/SortSelect';
import { byDate, byNumber, sortItems, thenBy, type SortOption } from '@/lib/sort';

const QUOTE_SORT: SortOption<Quote>[] = [
  { value: 'recent', label: 'Más recientes', compare: byDate((q) => q.createdAt, 'desc') },
  { value: 'oldest', label: 'Más antiguas', compare: byDate((q) => q.createdAt) },
  { value: 'expires', label: 'Vencen primero', compare: byDate((q) => q.validUntil) },
  { value: 'total_desc', label: 'Total mayor', compare: byNumber((q) => q.totalMinorUsd, 'desc') },
  { value: 'total_asc', label: 'Total menor', compare: byNumber((q) => q.totalMinorUsd) },
  {
    value: 'status',
    label: 'Estado',
    compare: thenBy<Quote>(
      (a, b) => statusLabel('quote', a.status).localeCompare(statusLabel('quote', b.status), 'es'),
      byDate((q) => q.createdAt, 'desc'),
    ),
  },
];

interface CotizacionesListPageProps {
  /** Group root for detail links, e.g. "/app/admin/cotizaciones". */
  basePath: string;
  title?: string;
  description?: string;
  /** Optional "Crear cotización" action target (sales/admin only). */
  createPath?: string;
  /**
   * When true (admin/finance), the backend rejects an unfiltered list, so every query
   * carries a concrete status; the "Todas" tab merges one query per status.
   */
  requireStatusFilter?: boolean;
}

/**
 * Cotizaciones list with status-filter tabs. Scoping is server-side (sales_rep →
 * assigned, admin/finance → all but with a required filter). A quote is priced
 * like an order but holds no stock. Money via shared helpers. Responsive: table
 * on desktop, cards on mobile.
 */
export function CotizacionesListPage({
  basePath,
  title = 'Cotizaciones',
  description = 'Cotizaciones y su estado.',
  createPath,
  requireStatusFilter = false,
}: CotizacionesListPageProps) {
  const canCreate = useCanCreateOrders();
  // Opens on "Todas".
  const [status, setStatus] = useState<QuoteStatus | undefined>(undefined);

  // The server refuses an unfiltered list for admin/finance, so their "Todas" merges one query per status.
  const mergeAll = requireStatusFilter && status === undefined;
  const single = useQuotes({ status, limit: 100 }, !mergeAll);
  const merged = useAllStatusList({
    scope: 'quotes',
    statuses: QUOTE_STATUS_TABS.flatMap((t) => (t.status ? [t.status] : [])),
    fetchStatus: (s) => api.listQuotes({ status: s, limit: 100 }),
    sortDate: (q) => q.createdAt,
    enabled: mergeAll,
  });
  const query = mergeAll
    ? { isLoading: merged.isLoading, isError: merged.isError, error: merged.error, refetch: merged.refetch }
    : single;
  const [sort, setSort] = useState('recent');
  const rawItems = mergeAll ? merged.items : (single.data?.items ?? []);
  const items = sortItems(rawItems, QUOTE_SORT, sort);

  // Customer names instead of ids; falls back to the id while loading or when not readable.
  const names = useAccountNames();
  const customerName = (id: string) => names.data?.get(id)?.name ?? id;

  const tabs = useMemo(() => [{ label: 'Todas', status: undefined }, ...QUOTE_STATUS_TABS], []);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={title}
        description={description}
        actions={
          createPath && canCreate ? (
            <Link to={createPath}>
              <Button size="sm">Crear cotización</Button>
            </Link>
          ) : undefined
        }
      />

      {/* Status filter tabs (horizontal scroll on mobile). */}
      <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="scrollbar-hover -mx-1 flex min-w-0 gap-2 overflow-x-auto px-1 pb-1">
        {tabs.map((tab) => {
          const active = tab.status === status;
          return (
            <button
              key={tab.label}
              type="button"
              onClick={() => setStatus(tab.status)}
              aria-pressed={active}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm transition-colors ${
                active
                  ? 'border-brand-primary bg-brand-primary/10 font-medium text-brand-primary'
                  : 'border-border text-text-muted hover:border-border-strong hover:text-text-primary'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <SortSelect options={QUOTE_SORT} value={sort} onChange={setSort} />
      </div>

      <Card className="overflow-hidden">
        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={items.length === 0}
          loadingLabel="Cargando cotizaciones…"
          loadingVariant="table"
          skeletonCols={4}
          emptyTitle="No hay cotizaciones"
          emptyDescription="No se encontraron cotizaciones para este filtro."
        >
          {/* Desktop table */}
          <div className="hidden md:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                  <th className="px-4 py-3 font-medium">Cotización</th>
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">Vence</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 text-right font-medium">Total (USD)</th>
                </tr>
              </thead>
              <tbody>
                {items.map((q) => (
                  <tr
                    key={q.quoteId}
                    className="border-b border-border last:border-0 hover:bg-surface-muted"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`${basePath}/${q.quoteId}`}
                        className="font-mono text-brand-primary hover:underline"
                      >
                        {quoteLabel(q)}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-text-muted">{customerName(q.customerId)}</td>
                    <td className="px-4 py-3 text-text-muted">{formatDate(q.validUntil)}</td>
                    <td className="px-4 py-3">
                      <StatusPill kind="quote" value={q.status} />
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-text-primary">
                      {formatMoney(q.totalMinorUsd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="divide-y divide-border md:hidden">
            {items.map((q) => (
              <li key={q.quoteId}>
                <Link
                  to={`${basePath}/${q.quoteId}`}
                  className="flex flex-col gap-2 p-4 active:bg-surface-muted"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-mono text-sm text-brand-primary">{quoteLabel(q)}</span>
                    <StatusPill kind="quote" value={q.status} />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-muted">
                      {customerName(q.customerId)}
                      {q.validUntil ? ` · Vence ${formatDate(q.validUntil)}` : ''}
                    </span>
                    <span className="font-semibold text-text-primary">
                      {formatMoney(q.totalMinorUsd)}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}

/** Compact local date (es-VE). `—` when absent; raw value if unparseable. */
function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
}
